import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { docTemplatesAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, FileText, Trash2, Pencil, Files, Clock } from 'lucide-react';

export default function DocTemplates() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try { const res = await docTemplatesAPI.list(); setTemplates(res.data || []); }
    catch { toast.error('Failed to load templates'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    try {
      const res = await docTemplatesAPI.create();
      navigate(`/admin/doc-templates/${res.data.id}`);
    } catch { toast.error('Failed to create template'); }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this template?')) return;
    try { await docTemplatesAPI.remove(id); toast.success('Deleted'); load(); }
    catch { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="animate-pulse space-y-4">{[1,2,3].map(i => <div key={i} className="h-20 bg-gray-100 rounded-lg" />)}</div>;

  return (
    <div data-testid="doc-templates-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Files className="w-5 h-5 text-[#E31937]" /> Document Templates
          </h1>
          <p className="text-sm text-gray-500 mt-1">Create reusable templates with drag-and-drop fields</p>
        </div>
        <Button className="bg-[#E31937] hover:bg-[#C4122C] text-white" onClick={handleCreate} data-testid="create-template-btn">
          <Plus className="w-4 h-4 mr-1" /> New Template
        </Button>
      </div>

      {templates.length === 0 ? (
        <Card className="border-gray-200">
          <CardContent className="p-12 text-center">
            <Files className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-base font-semibold text-gray-700 mb-1">No Templates Yet</h3>
            <p className="text-sm text-gray-400 mb-4">Upload a PDF and add signature, text, and other fields</p>
            <Button className="bg-[#E31937] hover:bg-[#C4122C] text-white" onClick={handleCreate}>
              <Plus className="w-4 h-4 mr-1" /> Create Your First Template
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map(t => (
            <Card
              key={t.id}
              className="border-gray-200 hover:border-[#E31937]/40 hover:shadow-md transition-all cursor-pointer group"
              onClick={() => navigate(`/admin/doc-templates/${t.id}`)}
              data-testid={`template-card-${t.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-lg bg-[#E31937]/10 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-[#E31937]" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold text-gray-900 truncate group-hover:text-[#E31937]">{t.name}</h3>
                      {t.description && <p className="text-xs text-gray-400 truncate">{t.description}</p>}
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600" onClick={(e) => handleDelete(t.id, e)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
                <div className="flex items-center gap-3 text-[11px] text-gray-500">
                  <Badge variant="outline" className={`text-[10px] ${t.page_count > 0 ? 'border-green-300 text-green-600' : 'border-gray-300 text-gray-400'}`}>
                    {t.page_count > 0 ? `${t.page_count} pages` : 'No PDF'}
                  </Badge>
                  <span>{(t.fields || []).length} fields</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(t.updated_at || t.created_at).toLocaleDateString()}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
